import useSound from "use-sound";
import { useQuery } from "react-query";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";

import { SkinAssetType } from "@app/api/models/user";
import { useDefaultProducts } from "@app/api/product";
import { getNumberOfCharacter, isDuplicateCharacter } from "@app/utils/character";

import { DefaultRouteProps, deviceType } from "@app/App";

import styles from "@app/assets/styles/lobby.module.scss";

import { GameImages } from "@app/assets/images/game/game";

import { CharacterSound, User } from "@app/types/user";

import { QUIZZ_SERVER_URL } from "@app/config";

import Context from "@app/contexts/Context";

export default function Lobby(props: DefaultRouteProps) {
  const { receiverContext } = props;

  const { data } = useDefaultProducts();

  const {
    ioSocket,
    roomId,
    setIoSocket,
    setRoomId
  } = useContext(Context);

  const gameUrl = "quizzdegenie://private/join?roomIdDeeplink=" + roomId;
  const navigate = useNavigate();
  const [players, setPlayers] = useState<User[]>([]);

  // Sound
  const charactersSound: CharacterSound = {
      astronaut: useSound(require("../assets/sound/lobby/astronautLobby.m4a"))[0],
      cat: useSound(require("../assets/sound/lobby/catLobby.m4a"))[0],
      catWoman: useSound(require("../assets/sound/lobby/catWomanLobby.m4a"))[0],
      punk: useSound(require("../assets/sound/lobby/punkLobby.m4a"))[0],
      viking: useSound(require("../assets/sound/lobby/vikingLobby.m4a"))[0],
      death: useSound(require("../assets/sound/lobby/deathLobby.m4a"))[0],
      alien: useSound(require("../assets/sound/lobby/alienLobby.m4a"))[0],
      physicist: useSound(require("../assets/sound/lobby/physicistLobby.m4a"))[0],
    };

  const lobbyMusic = useSound(require("../assets/sound/menu/lobbyLoop.m4a"), { loop: true, autoplay: true });

  //Query
  const playersQuery = useQuery(["get", "players"], async () => {
      const res = await fetch(`${QUIZZ_SERVER_URL}/rooms/users/room-${roomId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      const players: User[] = await res.json();
      setPlayers(players);
    },
    { keepPreviousData: false }
  );

  // Functions
  function userLogout(user: User) {
    if (user.leader) {

      if (ioSocket) {
        ioSocket.emit("tvDisconnected");
        ioSocket.disconnect();
        setIoSocket(undefined);
        setRoomId(undefined);
        setPlayers([]);
      }

      if (receiverContext) {
        receiverContext.stop();
      } else {
        navigate("/");
      }
    } else {
      playersQuery.refetch();
    }
  };

  function updateUser(user: User) {
    if (user && user.character) {
      charactersSound[user.character]();
    }
    playersQuery.refetch();
  };

  // Effects
  useEffect(() => {
    if (ioSocket) {
      const exitRoom = () => {
        lobbyMusic[1].stop();
        setRoomId(undefined);
        ioSocket.emit("tvDisconnected");
        ioSocket.disconnect();
        setIoSocket(undefined);

        if (receiverContext) {
          receiverContext.stop();
        } else {
          navigate("/");
        }
      };

      const soloOrTournamentStart = () => {
        lobbyMusic[1].stop();
        navigate(`/game`);
        ioSocket.off("deleteRoom", exitRoom);
        ioSocket.off('userLogin', updateUser);
        ioSocket.off('userLogout', userLogout);
        ioSocket.off("soloStart", soloOrTournamentStart);
        ioSocket.off("tournamentStart", soloOrTournamentStart);
      }

      ioSocket.on("deleteRoom", exitRoom);
      ioSocket.on('userLogin', updateUser);
      ioSocket.on('userLogout', userLogout);
      ioSocket.on("soloStart", soloOrTournamentStart);
      ioSocket.on("tournamentStart", soloOrTournamentStart);

      return () => {
        ioSocket.off("deleteRoom", exitRoom);
        ioSocket.off('userLogin', updateUser);
        ioSocket.off('userLogout', userLogout);
        ioSocket.off("soloStart", soloOrTournamentStart);
        ioSocket.off("tournamentStart", soloOrTournamentStart);
      };
    } else {
        if (deviceType !== "cast") {
          lobbyMusic[1].stop();
          setRoomId(undefined);
          setIoSocket(undefined);
          navigate("/");
        }
    }
  }, [ioSocket, lobbyMusic]);

  useEffect(() => {
    playersQuery.refetch();
  }, [roomId]);

  return (
    <div className={styles.lobbyContainer} style={{ background: `url(${GameImages.lobby.url}) center / contain no-repeat`}}>
      <div className={styles.roomInfo}>
        <QRCodeSVG includeMargin={true} size={window.innerHeight / 8} value={gameUrl} bgColor="#E4C67F" fgColor="#2B3B76" />
        Room Id: {roomId}
      </div>
      {players && players?.map((player, index) => {
        if (!player.character) return;

        const isDuplicate = isDuplicateCharacter(players, player);
        if (isDuplicate) return;

        let className;
        const nbOfSameCharacter = getNumberOfCharacter(players, player);

        const characterImg = data?.characters
          .find((character) => {
            const id = character.id?.split("-")[0];
            if (id) return id === player.character;
          })
          ?.products.find((product) => product.id === player.characterSkinId)
          ?.assets.find((asset) => asset.type === SkinAssetType.Lobby);

        switch (player.character) {
          case "viking":
            className = styles.viking;
            break;
          case "cat":
            className = styles.cat;
            break;
          case "catWoman":
            className = styles.catWoman;
            break;
          case "astronaut":
            className = styles.astronaut;
            break;
          case "punk":
            className = styles.punk;
            break;
          case "alien":
            className = styles.alien;
            break;
          case "death":
            className = styles.death;
            break;
          case "physicist":
            className = styles.physicist;
            break;
          default:
            break;
        }

        return (
          <div key={index + player.character} className={className} style={{ background: `url(${characterImg?.url}) center / contain no-repeat` }}>
            {nbOfSameCharacter > 1 && (
              <div className={styles.characterNumber}>
                {nbOfSameCharacter}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
